import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Home, ArrowLeft, ArrowRight } from 'lucide-react';
import { getConceptoBySlug, getConceptoPrevNext, ADO_ARTICULOS } from '../data/adoConceptos';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';

/* ── Placeholder de imagen ────────────────────────────────── */
const ConceptoImage = ({ imagen }) => {
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState(false);

  if (!error && imagen?.path) {
    return (
      <div className="w-full aspect-video rounded-xl overflow-hidden my-8 sm:my-10 bg-[#FEFBF4]">
        <img
          src={imagen.path}
          alt={imagen.alt}
          className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
        />
        {!loaded && <PlaceholderImagen alt={imagen.alt} />}
      </div>
    );
  }

  return <PlaceholderImagen alt={imagen?.alt} />;
};

const PlaceholderImagen = ({ alt }) => (
  <div className="w-full aspect-video rounded-xl border border-dashed border-[#C17B00]/40 bg-[#FEFBF4] flex flex-col items-center justify-center gap-3 my-8 sm:my-10">
    <div className="w-10 h-10 rounded-full border-2 border-dashed border-[#C17B00]/30 flex items-center justify-center">
      <span className="text-[#C17B00]/40 text-[9px] font-bold tracking-widest uppercase">img</span>
    </div>
    {alt && (
      <p className="text-[12px] text-[#C17B00]/55 font-medium text-center max-w-xs px-4 leading-relaxed">
        {alt}
      </p>
    )}
  </div>
);

/* ── Referencias cruzadas a artículos ───────────────────────── */
const CrossRefs = ({ refs }) => {
  if (!refs || refs.length === 0) return null;
  return (
    <div className="mt-10 pt-6 border-t border-[#E5E9EF]">
      <p className="text-[11px] font-bold tracking-[0.16em] text-[#8793A3] uppercase mb-3">
        Aparece en
      </p>
      <div className="flex flex-wrap gap-2">
        {refs.map((ref) => {
          const art = ADO_ARTICULOS[ref];
          if (!art) return (
            <span key={ref} className="text-[13px] text-[#534AB7] italic font-medium">{ref}</span>
          );
          return (
            <Link
              key={ref}
              to={`/colecciones/ado/${art.slug}`}
              className="inline-flex items-center gap-1.5 text-[12px] sm:text-[13px] text-[#534AB7] italic font-medium hover:text-[#3b2fa0] transition-colors border border-[#534AB7]/20 hover:border-[#534AB7]/50 rounded-md px-2.5 py-1"
              title={art.titulo}
            >
              {ref}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

/* ── Componente principal ─────────────────────────────────── */
const ColeccionADOConcepto = () => {
  const { conceptoSlug } = useParams();
  const navigate = useNavigate();

  const concepto = getConceptoBySlug(conceptoSlug);
  const { prev, next } = concepto
    ? getConceptoPrevNext(conceptoSlug)
    : { prev: null, next: null };

  useEffect(() => {
    if (!concepto) {
      navigate('/colecciones/ado/conceptos', { replace: true });
    }
  }, [concepto, navigate]);

  if (!concepto) return null;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={`${concepto.nombre} — Notas ADO | Edutech Pulse Lab`}
        description={`${concepto.tension} ${concepto.definicion}`}
        canonicalUrl={`/colecciones/ado/conceptos/${concepto.slug}`}
      />
      <Navbar />

      {/* ── Barra superior ADO ──────────────────────────────── */}
      <div className="bg-[#0c203b] relative">
        <div className="absolute top-0 left-0 w-[40%] h-[3px] bg-[#C4933A]" />
        <div className="max-w-[680px] mx-auto px-5 sm:px-8 py-5 relative">
          <nav className="flex items-center gap-1.5 text-[11px] text-white/35 flex-wrap">
            <Link to="/" className="hover:text-white/60 transition-colors" aria-label="Inicio">
              <Home className="w-3 h-3" />
            </Link>
            <ChevronRight className="w-3 h-3 text-white/15" />
            <Link to="/colecciones" className="hover:text-white/60 transition-colors">Colecciones</Link>
            <ChevronRight className="w-3 h-3 text-white/15" />
            <Link to="/colecciones/ado" className="hover:text-white/60 transition-colors">ADO</Link>
            <ChevronRight className="w-3 h-3 text-white/15" />
            <Link to="/colecciones/ado/conceptos" className="hover:text-white/60 transition-colors">Conceptos</Link>
            <ChevronRight className="w-3 h-3 text-white/15" />
            <span className="text-white/20 truncate max-w-[120px]">{concepto.nombre}</span>
          </nav>
        </div>
      </div>

      {/* ── Contenido principal ─────────────────────────────── */}
      <article className="bg-white">
        <div className="max-w-[680px] mx-auto px-5 sm:px-8 pt-10 pb-14 sm:pt-12 sm:pb-16">

          {/* Etiqueta "NOTAS ADO · concepto XX de 10" */}
          <p className="text-[11px] font-bold tracking-[0.2em] text-[#8793A3] uppercase mb-5">
            Notas ADO · Concepto {concepto.num} de 10
          </p>

          {/* Título H1 */}
          <h1 className="text-[32px] sm:text-[40px] lg:text-[44px] font-bold text-[#0F2844] leading-[1.1] mb-4 tracking-tight">
            {concepto.nombre}
          </h1>

          {/* Tensión / subtítulo */}
          <p className="text-[18px] sm:text-[20px] text-[#5F5E5A] italic leading-[1.55] mb-7">
            {concepto.tension}
          </p>

          {/* Separador */}
          <div className="w-12 h-[2px] bg-[#C4933A]/40 mb-8" />

          {/* Imagen */}
          <ConceptoImage imagen={concepto.imagen} />

          {/* Cuerpo */}
          {concepto.cuerpo.map((parrafo, idx) => (
            <p
              key={idx}
              className="text-[17px] sm:text-[18px] text-[#1A1A1A] leading-[1.75] mb-5"
            >
              {parrafo}
            </p>
          ))}

          {/* Definición formal — púrpura, bold italic, indentada */}
          <div className="my-8 pl-8">
            <p className="text-[17px] sm:text-[18px] text-[#534AB7] font-bold italic leading-[1.7]">
              {concepto.definicion}
            </p>
          </div>

          {/* Frase de cierre — navy, bold italic */}
          <p className="text-[17px] sm:text-[18px] text-[#0F2844] font-bold italic leading-[1.7] mt-6">
            {concepto.cierre}
          </p>

          {/* Referencias cruzadas */}
          <CrossRefs refs={concepto.apareceEn} />
        </div>
      </article>

      {/* ── Navegación entre conceptos ───────────────────────── */}
      <nav className="border-t border-slate-100 bg-slate-50/60">
        <div className="max-w-[680px] mx-auto px-5 sm:px-8 py-7 sm:py-8">
          <div className="flex flex-col sm:flex-row gap-5 sm:gap-0 sm:justify-between">
            {prev ? (
              <Link
                to={`/colecciones/ado/conceptos/${prev.slug}`}
                className="group flex items-start gap-3 sm:max-w-[46%]"
              >
                <ArrowLeft className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0 group-hover:-translate-x-0.5 transition-transform duration-200" />
                <div>
                  <p className="text-[10px] font-bold tracking-[0.16em] text-slate-400 uppercase mb-1">
                    Concepto anterior
                  </p>
                  <p className="text-[13px] sm:text-[14px] font-medium text-[#0F2844] group-hover:text-slate-500 transition-colors leading-snug">
                    {prev.num}. {prev.nombre}
                  </p>
                </div>
              </Link>
            ) : <div />}

            {next ? (
              <Link
                to={`/colecciones/ado/conceptos/${next.slug}`}
                className="group flex items-start gap-3 sm:max-w-[46%] sm:text-right sm:flex-row-reverse"
              >
                <ArrowRight className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0 group-hover:translate-x-0.5 transition-transform duration-200" />
                <div>
                  <p className="text-[10px] font-bold tracking-[0.16em] text-slate-400 uppercase mb-1">
                    Concepto siguiente
                  </p>
                  <p className="text-[13px] sm:text-[14px] font-medium text-[#0F2844] group-hover:text-slate-500 transition-colors leading-snug">
                    {next.num}. {next.nombre}
                  </p>
                </div>
              </Link>
            ) : <div />}
          </div>
        </div>
      </nav>

      {/* ── CTA ─────────────────────────────────────────────── */}
      <section className="bg-[#0c203b]">
        <div className="max-w-[680px] mx-auto px-5 sm:px-8 py-9 sm:py-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold tracking-[0.2em] text-white/25 uppercase mb-1.5">
              Notas ADO
            </p>
            <p className="text-[14px] text-white/55 leading-snug">
              Vocabulario de Arquitectura de Decisión Organizacional
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/colecciones/ado/conceptos"
              className="inline-flex items-center gap-1.5 text-[12px] sm:text-[13px] font-semibold text-white/60 hover:text-white border border-white/15 hover:border-white/35 px-4 py-2 rounded-lg transition-all duration-200 justify-center"
            >
              Ver todos los conceptos
            </Link>
            <Link
              to="/colecciones/ado"
              className="group inline-flex items-center gap-1.5 text-[12px] sm:text-[13px] font-semibold text-white border border-white/20 hover:border-white/50 px-4 py-2 rounded-lg transition-all duration-200 justify-center"
            >
              Colección ADO
              <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ColeccionADOConcepto;
